import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useUser } from './UserContext';

const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const CLEAN_ENV_BASE = ENV_BASE.replace(/\/+$/, '');
const FALLBACK_BASE = "https://www.doroosonline.co.il";
const API_BASE = CLEAN_ENV_BASE || FALLBACK_BASE;

const DEFAULT_TIMEOUT_MS = 12000;
const DEFAULT_TIMEOUT_MS_POST = 7000;
const DEFAULT_TIMEOUT_MS_PATCH = 10000;
const url = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

const MOBILE_MEDIA_QUERY = '(max-width: 639px)';
const MOBILE_PAGE_LIMIT = 4;
const DESKTOP_PAGE_LIMIT = 6;

const getResponsiveLimit = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return DESKTOP_PAGE_LIMIT;
  }
  return window.matchMedia(MOBILE_MEDIA_QUERY).matches
    ? MOBILE_PAGE_LIMIT
    : DESKTOP_PAGE_LIMIT;
};

async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const text = await res.text().catch(() => '');
  if (!text) return {};
  try { return JSON.parse(text); } catch { return { raw: text }; }
}

async function apiGet(
  path,
  { timeout = DEFAULT_TIMEOUT_MS, headers = {}, signal: externalSignal } = {}
) {
  const controller = externalSignal ? null : new AbortController();
  const signal = externalSignal || controller.signal;

  let didTimeout = false;

  const t = setTimeout(() => {
    didTimeout = true;
    if (controller) controller.abort();
  }, timeout);

  try {
    const res = await fetch(url(path), {
      method: 'GET',
      mode: 'cors',
      cache: 'no-store',
      credentials: 'omit',
      signal,
      headers,
    });

    const data = await parseJSONSafely(res);

    if (!res.ok) {
      const msg =
        data?.error ||
        data?.message ||
        (res.status === 401 ? 'שגיאה בהרשאות (401)' : `שגיאת שרת (${res.status})`);

      const err = new Error(msg);
      err.status = res.status;
      err.payload = data;
      throw err;
    }

    return data ?? {};
  } catch (e) {

    if (e.name === 'AbortError') {
      if (didTimeout) {
        const err = new Error('REQUEST_TIMEOUT');
        err.code = 'REQUEST_TIMEOUT';
        throw err;
      }

      const err = new Error('REQUEST_ABORTED');
      err.code = 'REQUEST_ABORTED';
      throw err;
    }

    if (e instanceof TypeError) {
      const hint = API_BASE
        ? `בדוק שהשרת זמין ב- ${API_BASE} ושCORS מאפשר את המקור של הלקוח.`
        : 'הגדר VITE_API_BASE/REACT_APP_API_BASE כדי לוודא שהלקוח פונה לשרת הנכון.';
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת. ${hint}`);
    }

    throw e;
  } finally {
    clearTimeout(t);
  }
}

async function apiGetAuth(
  path,
  { timeout = DEFAULT_TIMEOUT_MS, token, signal } = {}
) {
  let tk = token;

  if (!tk) {
    try {
      tk =
        localStorage.getItem('jwt') ??
        localStorage.getItem('token') ??
        null;
    } catch {
      tk = null;
    }
  }

  if (!tk) {
    console.warn('[SearchContext] apiGetAuth called without token for path:', path);
    return apiGet(path, { timeout, signal });
  }

  return apiGet(path, {
    timeout,
    signal,
    headers: {
      Authorization: `Bearer ${tk}`,
      Accept: 'application/json',
    },
  });
}

const toQuery = (obj = {}) =>
  Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');


const byId = (arr) => {
  const m = new Map();
  for (const c of arr) m.set(c.id, c);
  return m;
};

const upsert = (arr, item) => {
  const m = byId(arr);
  m.set(item.id, { ...m.get(item.id), ...item });
  return Array.from(m.values());
};

const SearchContext = createContext(null);

export const SearchProvider = ({ children }) => {
  const { user, role, userType, showAlert } = useUser();

  const token = useMemo(
    () => localStorage.getItem('jwt') ?? localStorage.getItem('token') ?? null,
    [user]
  );

  const isTeacher = role === 'teacher' || userType === 'teacher';
  const teacherId = isTeacher ? user?.id ?? null : null;

  // Learning center (GEFEN supplier). "My courses" for a supplier means
  // every course they created — public (SUPPLIER_INDEPENDENT) or inside
  // one of their GEFEN programs (PROGRAM) — regardless of which team
  // teacher is assigned to teach it.
  const isSupplier = role === 'supplier' || userType === 'supplier';
  const supplierId = isSupplier ? user?.id ?? null : null;

  // School. "My courses" for a school means the courses that belong to
  // whichever GEFEN programs the school is registered in
  // (school_programs -> programs -> courses where ownership_type=PROGRAM),
  // not courses the school created itself.
  const isSchool = role === 'school' || userType === 'school';
  const schoolId = isSchool ? user?.id ?? null : null;

  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [committedTerm, setCommittedTerm] = useState('');
  const [submitTick, setSubmitTick] = useState(0);
  const [searchMode, setSearchMode] = useState('quick');
  const [filters, setFilters] = useState({
    courseTypes: [],
    subject: '',
    ageGroup: '',
  });

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getResponsiveLimit);
  const [total, setTotal] = useState(0);
  const [loadingCourses, setLoadingCourses] = useState(false);

  // Narrows fetchCourses() down to a single GEFEN program's own courses
  // (courses.program_id) — set by searchByProgram(), e.g. from the
  // student wallet's "register for this program's courses" button.
  // null means "no program filter", i.e. the normal, unfiltered search.
  const [programFilter, setProgramFilter] = useState(null);

  const [myCoursesPage, setMyCoursesPage] = useState(1);
  const [myCoursesLimit, setMyCoursesLimit] = useState(getResponsiveLimit);
  const [myCoursesTotalActive, setMyCoursesTotalActive] = useState(0);
  const [myCoursesTotalArchived, setMyCoursesTotalArchived] = useState(0);
  const [loadingMyCourses, setLoadingMyCourses] = useState(false);
  const myCoursesLoadingCount = useRef(0);

  const [myCoursesAll, setMyCoursesAll] = useState([]);
  const [registeredCourses, setRegisteredCourses] = useState([]);
  const registeredSet = useMemo(() => new Set(registeredCourses), [registeredCourses]);
  const [registeredDateRange, setRegisteredDateRange] = useState({
    start: '',
    end: '',
  });

  const myActiveCourses = useMemo(
    () => myCoursesAll.filter((c) => c._position === 'ACTIVE'),
    [myCoursesAll]
  );
  const myArchivedCourses = useMemo(
    () => myCoursesAll.filter((c) => c._position === 'ARCHIVED'),
    [myCoursesAll]
  );

  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState('');

  const [courseImageCollections, setCourseImageCollections] = useState([]);
  const [courseImagesLoading, setCourseImagesLoading] = useState(false);
  const [courseImagesError, setCourseImagesError] = useState('');

  const didMountLimitEffect = useRef(false);

  const myCoursesAbortControllers = useRef({
    ACTIVE: null,
    ARCHIVED: null,
  });

  const fetchMyCoursesPage = useCallback(async ({
    position = 'ACTIVE',
    page = 1,
    limit = myCoursesLimit,
    range,
  } = {}) => {
    if (!token) return [];

    const safePosition = position === 'ARCHIVED' ? 'ARCHIVED' : 'ACTIVE';
    const effectiveRange = range ?? { start: '', end: '' };

    if (myCoursesAbortControllers.current[safePosition]) {
      myCoursesAbortControllers.current[safePosition].abort();
    }

    const controller = new AbortController();
    myCoursesAbortControllers.current[safePosition] = controller;

    // The backend decides which query to run based on these flags:
    //  - isTeacher + teacherId  -> courses where teacher_id = teacherId
    //    (covers both self-created TEACHER_INDEPENDENT courses AND
    //    courses a learning center created and assigned this teacher to)
    //  - isSupplier + supplierId -> courses where created_by_user_id =
    //    supplierId (SUPPLIER_INDEPENDENT + PROGRAM courses they made)
    //  - isSchool + schoolId -> courses on programs the school is
    //    registered in via school_programs (ownership_type=PROGRAM)
    //  - none of the above -> the student's registered courses
    //    (course_registrations), same as before this change
    const params = {
      position: safePosition,
      page,
      limit,
      isTeacher: isTeacher ? 1 : 0,
      ...(isTeacher && teacherId ? { teacherId } : {}),
      isSupplier: isSupplier ? 1 : 0,
      ...(isSupplier && supplierId ? { supplierId } : {}),
      isSchool: isSchool ? 1 : 0,
      ...(isSchool && schoolId ? { schoolId } : {}),
      ...(effectiveRange?.start ? { start: effectiveRange.start } : {}),
      ...(effectiveRange?.end ? { end: effectiveRange.end } : {}),
    };

    try {

      myCoursesLoadingCount.current += 1;
      setLoadingMyCourses(true);

      const data = await apiGetAuth(`/api/my/courses?${toQuery(params)}`, {
        token,
        signal: controller.signal,
      });

      if (myCoursesAbortControllers.current[safePosition] !== controller) {
        return [];
      }

      const items = Array.isArray(data?.items) ? data.items : [];
      const total = Number(data?.total || items.length || 0);

      const tagged = items.map((c) => ({ ...c, _position: safePosition }));

      setMyCoursesAll((prev) => {
        const others = prev.filter((c) => c._position !== safePosition);
        return [...others, ...tagged];
      });

      setRegisteredCourses((prev) => {
        const ids = new Set(prev);
        tagged.forEach((c) => ids.add(c.id));
        return Array.from(ids);
      });

      if (safePosition === 'ACTIVE') {
        setMyCoursesTotalActive(total);
      } else {
        setMyCoursesTotalArchived(total);
      }

      setMyCoursesPage(Number(data?.page || page));
      setMyCoursesLimit(Number(data?.limit || limit));

      return tagged;
    } catch (err) {

      if (err?.code === 'REQUEST_ABORTED') {
        return [];
      }

      if (err?.code === 'REQUEST_TIMEOUT') {
        throw new Error('זמן ההמתנה לטעינת הקורסים הסתיים. נסה שוב.');
      }

      throw err;

    } finally {
      if (myCoursesAbortControllers.current[safePosition] === controller) {
        myCoursesAbortControllers.current[safePosition] = null;
      }
      myCoursesLoadingCount.current = Math.max(0, myCoursesLoadingCount.current - 1);
      setLoadingMyCourses(myCoursesLoadingCount.current > 0);
    }
  }, [
    token,
    isTeacher,
    teacherId,
    isSupplier,
    supplierId,
    isSchool,
    schoolId,
    myCoursesLimit,
  ]);

  const fetchCourses = useCallback(
  async ({ page: p = 1, limit: l = limit } = {}) => {
    try {
      setLoadingCourses(true);

      let path = '/api/courses';
      const baseQuery = {
        page: p,
        limit: l,
        status: 'Opened',
      };

      // A program filter (set by searchByProgram, e.g. from the wallet's
      // "register for this program" button) applies on top of whichever
      // search mode is active, quick or advanced.
      if (programFilter) {
        baseQuery.programId = programFilter;
      }

      const isQuick = searchMode === 'quick';
      const hasTerm = !!committedTerm;

      if (isQuick) {
        if (hasTerm) {
          baseQuery.q = committedTerm;
        }
      } else {
        path = '/api/courses/search';

        const selectedType =
          (filters.courseTypes && filters.courseTypes[0]) || '';

        if (filters.subject) baseQuery.subject = filters.subject;
        if (selectedType) baseQuery.type = selectedType;
        if (filters.ageGroup) baseQuery.audience = filters.ageGroup;
      }

      const qs = toQuery(baseQuery);
      const fullPath = qs ? `${path}?${qs}` : path;

      const data = await apiGet(fullPath);

      let items = [];
      if (Array.isArray(data?.items)) {
        items = data.items;
      } else if (Array.isArray(data)) {
        items = data;
      } else if (Array.isArray(data?.courses)) {
        items = data.courses;
      }

      setFilteredCourses(items);
      setTotal(typeof data?.total === 'number' ? data.total : items.length);
      setPage(Number(data?.page || p));
      setLimit(Number(data?.limit || l));
    } catch (err) {
      console.error('❌ fetchCourses error:', err);
      showAlert?.(
        err.message || 'שגיאה בטעינת תוצאות מהשרת. נסו שוב מאוחר יותר.',
        'warning'
      );
      setFilteredCourses([]);
      setTotal(0);
    } finally {
      setLoadingCourses(false);
    }
  },
  [searchMode, committedTerm, filters, limit, showAlert, programFilter]
);

  const goToPage = useCallback(
    (p) => {
      const nextPage = Math.max(1, Number(p) || 1);
      setPage(nextPage);
      fetchCourses({ page: nextPage, limit });
    },
    [fetchCourses, limit]
  );

  // Jumps the search results to a single GEFEN program's own courses —
  // used by the student wallet's "register for courses in this program"
  // button so it lands the student on already-filtered results instead
  // of a blank search box they'd have to figure out on their own.
  const searchByProgram = useCallback((programId) => {
    const pid = Number(programId) > 0 ? Number(programId) : null;
    setProgramFilter(pid);
    setSearchTerm('');
    setCommittedTerm('');
    setSearchMode('quick');
    setFilters({ courseTypes: [], subject: '', ageGroup: '' });
    setPage(1);
    setSubmitTick((t) => t + 1);
  }, []);

  const loadMyCoursesOnce = useCallback(async () => {
    if (!token) return;

    await Promise.all([
      fetchMyCoursesPage({
        position: 'ACTIVE',
        page: 1,
        limit: myCoursesLimit,
        range: { start: '', end: '' },
      }),
      fetchMyCoursesPage({
        position: 'ARCHIVED',
        page: 1,
        limit: myCoursesLimit,
        range: { start: '', end: '' },
      }),
    ]);
  }, [token, fetchMyCoursesPage, myCoursesLimit]);

  const reloadMyCourses = useCallback(async () => {
    await fetchMyCoursesPage({
      position: 'ACTIVE',
      page: myCoursesPage,
      limit: myCoursesLimit,
      range: registeredDateRange,
    });

    await fetchMyCoursesPage({
      position: 'ARCHIVED',
      page: 1,
      limit: myCoursesLimit,
      range: registeredDateRange,
    });
  }, [fetchMyCoursesPage, myCoursesPage, myCoursesLimit, registeredDateRange]);

  const goToMyCoursesPage = useCallback(async (nextPage, position = 'ACTIVE') => {
    const safePage = Math.max(1, Number(nextPage) || 1);
    await fetchMyCoursesPage({
      position,
      page: safePage,
      limit: myCoursesLimit,
      range: registeredDateRange,
    });
  }, [fetchMyCoursesPage, myCoursesLimit, registeredDateRange]);

  const handleRegister = useCallback(
    async (courseId) => {
      if (!token) throw new Error('נדרש להתחבר כדי להירשם לקורס');

      const res = await fetch(url(`/api/courses/${courseId}/register`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        mode: 'cors',
        credentials: 'omit',
      });

      const data = await parseJSONSafely(res);
      if (!res.ok) {
        const msg = data?.message || 'ההרשמה לקורס נכשלה.';
        if (res.status === 401) {
          throw new Error('ההתחברות חסרה או פגה. התחבר/י מחדש.');
        }
        throw new Error(msg);
      }

      await reloadMyCourses();
    },
    [token, reloadMyCourses]
  );

  const unregisterCourse = useCallback(
    async (courseId) => {
      if (!token) throw new Error('נדרש להתחבר כדי לבטל הרשמה');

      const res = await fetch(url(`/api/courses/${courseId}/register`), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        mode: 'cors',
        credentials: 'omit',
      });

      const data = await parseJSONSafely(res);
      if (!res.ok) {
        const msg = data?.message || 'ביטול הרשמה נכשל.';
        if (res.status === 401) {
          throw new Error('ההתחברות חסרה או פגה. התחבר/י מחדש.');
        }
        throw new Error(msg);
      }

      await reloadMyCourses();
    },
    [token, reloadMyCourses]
  );

  const addCourse = useCallback(
    async (newCourse) => {
      if (!token) throw new Error('נדרש להתחבר כדי ליצור קורס');

      const tempId = `tmp-${Date.now()}`;
      const optimistic = {
        ...newCourse,
        id: tempId,
        status: newCourse.status || 'Editing',
        _position: 'ACTIVE',
        _optimistic: true,
      };

      setCourses((prev) => [optimistic, ...prev]);
      setFilteredCourses((prev) => [optimistic, ...prev]);
      setMyCoursesAll((prev) => [optimistic, ...prev]);
      setRegisteredCourses((prev) => [tempId, ...prev]);

      const payload = {
        title: newCourse.title,
        description: newCourse.description ?? '',
        image: newCourse.image ?? '',
        subject: newCourse.subject,
        price: newCourse.price ?? null,
        duration: newCourse.duration ?? '',
        startDate: newCourse.startDate,
        studentCount: newCourse.studentCount ?? 0,
        rating: newCourse.rating ?? null,
        type: newCourse.type,
        status: newCourse.status ?? 'Editing',
        audience: newCourse.audience ?? 'all',
        schedule: newCourse.schedule ?? null,
        // CreateCourseModal falls back to a sentinel teacher object
        // ({ id: null, ... }) when the creator isn't a teacher — only
        // trust a real positive teacher id here, otherwise fall back to
        // the logged-in teacher's own id, or null. A supplier-created
        // course is expected to start with no teacher assigned yet
        // (courses.teacher_id is nullable) until a team-teacher picker
        // exists.
        teacherId:
          Number.isInteger(newCourse.teacher?.id) && newCourse.teacher.id > 0
            ? newCourse.teacher.id
            : (isTeacher ? teacherId ?? null : null),
        // Tags the course as the supplier's own SUPPLIER_INDEPENDENT
        // course rather than the default TEACHER_INDEPENDENT. The
        // backend re-derives/validates this from the authenticated
        // user's role rather than trusting it blindly (see
        // CoursesController::create()).
        ownershipType: isSupplier ? 'SUPPLIER_INDEPENDENT' : 'TEACHER_INDEPENDENT',
      };

      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS_POST);

      let res;
      let data;
      try {
        res = await fetch(url('/api/courses'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          signal: controller.signal,
        });
        data = await parseJSONSafely(res);
      } catch (e) {
        setCourses((prev) => prev.filter((c) => c.id !== tempId));
        setFilteredCourses((prev) => prev.filter((c) => c.id !== tempId));
        setMyCoursesAll((prev) => prev.filter((c) => c.id !== tempId));
        setRegisteredCourses((prev) => prev.filter((id) => id !== tempId));

        if (e.name === 'AbortError') {
          throw new Error('השרת לא הגיב בזמן (7 שניות).');
        }
        if (e instanceof TypeError) {
          const hint = API_BASE
            ? `בדוק שהשרת זמין ב-${API_BASE} וש-CORS מאפשר את המקור של הלקוח.`
            : 'הגדר VITE_API_BASE/REACT_APP_API_BASE כדי לוודא שהלקוח פונה לשרת הנכון.';
          throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת. ${hint}`);
        }
        throw e;
      } finally {
        clearTimeout(t);
      }

      if (!res.ok || !data?.course) {
        setCourses((prev) => prev.filter((c) => c.id !== tempId));
        setFilteredCourses((prev) => prev.filter((c) => c.id !== tempId));
        setMyCoursesAll((prev) => prev.filter((c) => c.id !== tempId));
        setRegisteredCourses((prev) => prev.filter((id) => id !== tempId));

        const text = data?.message || data?.raw || `HTTP ${res.status}`;
        if (res.status === 401) {
          throw new Error('ההתחברות חסרה או פגה. התחבר/י מחדש.');
        }
        throw new Error(`יצירת קורס נכשלה (${res.status}) ${text}`);
      }

      const created = { ...data.course, _position: 'ACTIVE' };

      setCourses((prev) => {
        const arr = prev.filter((c) => c.id !== tempId);
        return [created, ...arr];
      });

      setFilteredCourses((prev) => {
        const arr = prev.filter((c) => c.id !== tempId);
        return [created, ...arr];
      });

      setMyCoursesAll((prev) => {
        const arr = prev.filter((c) => c.id !== tempId);
        return upsert(arr, created);
      });

      setRegisteredCourses((prev) => [
        created.id,
        ...prev.filter((id) => id !== tempId),
      ]);

      return created;
    },
    [token, isTeacher, teacherId, isSupplier]
  );

  const updateCourse = useCallback(
    async (courseId, patch) => {
      if (!token) throw new Error('נדרש להתחבר כדי לערוך קורס');

      const controller = new AbortController();
      const t = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS_PATCH);

      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };

      let res = null;
      let data = null;

      try {
        res = await fetch(url(`/api/courses/${courseId}`), {
          method: 'PATCH',
          headers,
          body: JSON.stringify(patch),
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
          signal: controller.signal,
        });
        data = await parseJSONSafely(res);
      } catch (e) {
        if (e.name === 'AbortError') {
          throw new Error('השרת לא הגיב בזמן (10 שניות).');
        }
        throw e;
      } finally {
        clearTimeout(t);
      }

      if (res && res.status === 405) {
        const resPut = await fetch(url(`/api/courses/${courseId}`), {
          method: 'PUT',
          headers,
          body: JSON.stringify(patch),
          mode: 'cors',
          credentials: 'omit',
          cache: 'no-store',
        });
        const dataPut = await parseJSONSafely(resPut);

        if (!resPut.ok) {
          const msg = dataPut?.message || `עדכון נכשל (${resPut.status})`;
          if (resPut.status === 401) {
            throw new Error('ההתחברות חסרה או פגה. התחבר/י מחדש.');
          }
          throw new Error(msg);
        }

        const updatedFromServer = dataPut?.course || dataPut || {};

        const merged = (prev) =>
          prev?.id === courseId
            ? { ...prev, ...patch, ...updatedFromServer }
            : prev;

        setMyCoursesAll((prev) => prev.map(merged));
        setCourses((prev) => prev.map(merged));
        setFilteredCourses((prev) => prev.map(merged));

        return {
          ...updatedFromServer,
          id: updatedFromServer.id ?? courseId,
        };
      }

      if (!res.ok) {
        const text = data?.message || data?.raw || `HTTP ${res.status}`;
        if (res.status === 401) {
          throw new Error('ההתחברות חסרה או פגה. התחבר/י מחדש.');
        }
        throw new Error(`עדכון נכשל (${res.status}) ${text}`);
      }

      const updatedFromServer = data?.course || data || {};

      const merged = (prev) =>
        prev?.id === courseId
          ? { ...prev, ...patch, ...updatedFromServer }
          : prev;

      setMyCoursesAll((prev) => prev.map(merged));
      setCourses((prev) => prev.map(merged));
      setFilteredCourses((prev) => prev.map(merged));

      const localCopy = myCoursesAll.find((c) => c.id === courseId);
      const position = localCopy?._position ? { _position: localCopy._position } : {};

      return {
        ...position,
        ...localCopy,
        ...patch,
        ...updatedFromServer,
        id: updatedFromServer.id ?? courseId,
      };
    },
    [token, myCoursesAll]
  );

  const deleteCourse = useCallback(
    async (courseId) => {
      if (!token) throw new Error('נדרש להתחבר כדי למחוק קורס');

      const res = await fetch(url(`/api/courses/${courseId}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        mode: 'cors',
        credentials: 'omit',
      });

      const data = await parseJSONSafely(res);
      if (!res.ok) {
        const msg = data?.message || `מחיקה נכשלה (${res.status})`;
        if (res.status === 401) {
          throw new Error('ההתחברות חסרה או פגה. התחבר/י מחדש.');
        }
        throw new Error(msg);
      }

      setMyCoursesAll((prev) => prev.filter((c) => c.id !== courseId));
      setCourses((prev) => prev.filter((c) => c.id !== courseId));
      setFilteredCourses((prev) => prev.filter((c) => c.id !== courseId));
      setRegisteredCourses((prev) => prev.filter((id) => id !== courseId));
    },
    [token]
  );

  const refreshSubjects = useCallback(async (q = '') => {
    try {
      setSubjectsLoading(true);
      setSubjectsError('');

      const qs = toQuery({
        q: (q || '').toString().trim(),
        limit: 100,
      });

      const data = await apiGet(`/api/courses/subjects${qs ? `?${qs}` : ''}`);

      let items = [];
      if (Array.isArray(data?.items)) {
        items = data.items;
      } else if (Array.isArray(data)) {
        items = data;
      }

      const normalized = items
        .map((item) => ({
          id: item?.id ?? null,
          name: String(item?.name ?? '').trim(),
        }))
        .filter((item) => item.name !== '');

      setSubjects(normalized);
    } catch (err) {
      console.error('[Search] refreshSubjects error:', err);
      setSubjects([]);
      setSubjectsError(err.message || 'שגיאה בטעינת מקצועות מהשרת.');
    } finally {
      setSubjectsLoading(false);
    }
  }, []);

  const fetchCourseImageCollections = useCallback(async () => {
    try {
      setCourseImagesLoading(true);
      setCourseImagesError('');

      const data = await apiGet('/api/course-images');

      let collections = [];

      if (Array.isArray(data?.items)) {
        collections = data.items;
      } else if (Array.isArray(data?.collections)) {
        collections = data.collections;
      } else if (Array.isArray(data)) {
        collections = data;
      }

      setCourseImageCollections(collections);
    } catch (err) {
      console.error('[Search] fetchCourseImageCollections error:', err);
      setCourseImagesError(err.message || 'שגיאה בטעינת תמונות הקורס מהשרת.');
    } finally {
      setCourseImagesLoading(false);
    }
  }, []);

  const myCourses = useMemo(() => myActiveCourses, [myActiveCourses]);
  const isCourseRegistered = useCallback((id) => registeredSet.has(id), [registeredSet]);

  const getMyCoursesActive = useCallback(
    () => myActiveCourses,
    [myActiveCourses]
  );

  const getMyCoursesArchived = useCallback(
    () => myArchivedCourses,
    [myArchivedCourses]
  );


  const getCourseParticipants = useCallback(
    async (courseId) => {
      const data = await apiGetAuth(`/api/courses/${courseId}/participants`, { token });
      return Array.isArray(data?.participants) ? data.participants : [];
    },
    [token]
  );

  const getCourseSchedule = useCallback(async (courseId) => {
    try {
      const data = await apiGet(`/api/courses/${courseId}/schedule`);
      if (data && data.ok && Array.isArray(data.schedule)) return data.schedule;
      if (data && data.schedule) return data.schedule;
    } catch (_) {
      const data = await apiGet(`/api/courses/${courseId}`);
      if (data?.course?.schedule) return data.course.schedule;
    }
    return null;
  }, []);


  useEffect(() => {
    fetchCourses({ page: 1, limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (submitTick === 0) return;
    fetchCourses({ page: 1, limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitTick]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const media = window.matchMedia(MOBILE_MEDIA_QUERY);

    const applyResponsiveLimit = () => {
      const nextLimit = media.matches ? MOBILE_PAGE_LIMIT : DESKTOP_PAGE_LIMIT;

      setLimit((prev) => (prev === nextLimit ? prev : nextLimit));
      setMyCoursesLimit((prev) => (prev === nextLimit ? prev : nextLimit));
    };

    applyResponsiveLimit();

    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', applyResponsiveLimit);
      return () => media.removeEventListener('change', applyResponsiveLimit);
    }

    media.addListener(applyResponsiveLimit);
    return () => media.removeListener(applyResponsiveLimit);
  }, []);

  useEffect(() => {
    if (!didMountLimitEffect.current) {
      didMountLimitEffect.current = true;
      return;
    }

    setPage(1);
    fetchCourses({ page: 1, limit });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit]);

  useEffect(() => {
    if (!token) {
      setMyCoursesAll([]);
      setRegisteredCourses([]);
      return;
    }
    // Wait until we actually have the id we need to scope the query by,
    // same guard as before but now covering all three "owner" roles.
    if (isTeacher && !teacherId) return;
    if (isSupplier && !supplierId) return;
    if (isSchool && !schoolId) return;
    loadMyCoursesOnce();
  }, [
    token,
    isTeacher,
    teacherId,
    isSupplier,
    supplierId,
    isSchool,
    schoolId,
    loadMyCoursesOnce,
  ]);

  useEffect(() => {
    refreshSubjects('');
  }, [refreshSubjects]);

  useEffect(() => {
    fetchCourseImageCollections();
  }, [fetchCourseImageCollections]);

  const handleSearch = useCallback(
    (mode = 'quick') => {
      if (mode === 'quick') {
        setCommittedTerm((searchTerm || '').trim());
      }
      setSearchMode(mode);
      setPage(1);
      setSubmitTick((t) => t + 1);
    },
    [searchTerm]
  );

  const resetSearch = () => {
    setSearchTerm('');
    setCommittedTerm('');
    setFilters({ courseTypes: [], subject: '', ageGroup: '' });
    setSearchMode('quick');
    setProgramFilter(null);
    setPage(1);
    setSubmitTick((t) => t + 1);
  };

  const value = {
    courses,
    filteredCourses,
    page,
    limit,
    total,
    goToPage,
    loadingCourses,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    searchMode,
    handleSearch,
    resetSearch,

    programFilter,
    searchByProgram,

    myCoursesPage,
    myCoursesLimit,
    myCoursesTotalActive,
    myCoursesTotalArchived,
    loadingMyCourses,
    goToMyCoursesPage,
    fetchMyCoursesPage,

    registeredCourses,
    myCourses,
    isCourseRegistered,
    getMyCoursesActive,
    getMyCoursesArchived,
    registeredDateRange,
    setRegisteredDateRange,
    handleRegister,
    unregisterCourse,
    addCourse,
    updateCourse,
    deleteCourse,
    reloadMyCourses,

    getCourseParticipants,
    getCourseSchedule,

    subjects,
    subjectsLoading,
    subjectsError,
    refreshSubjects,

    courseImageCollections,
    courseImagesLoading,
    courseImagesError,
    refreshCourseImageCollections: fetchCourseImageCollections,
  };

  if (typeof window !== 'undefined' && !CLEAN_ENV_BASE) {
    console.warn(
      `[SearchContext] API base not set in env. Falling back to ${FALLBACK_BASE}. Set VITE_API_BASE / REACT_APP_API_BASE for development.`
    );
  }

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error('useSearch must be used within a SearchProvider');
  return ctx;
};